import type { FC, MouseEventHandler } from 'react';
import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { extendAnalyticsData, getIsLineFinished, selectCouponSelectedBetsV2, selectUserIsAuthenticated, sendAnalyticsData, useLineBehaviorV2, useWebsocketLineShort } from '@mwl/core-lib';
import { MatchTeam } from '@/components/MatchTeam/MatchTeam';
import { base, routes } from '@/constants';
import { useMediaWidthDown, useTypedSelector } from '@/hooks';
import { getCompetitorsLineName } from '@/utils';
import type { LineRowProps, LineWithEntities } from '@/types';
import { MatchStatus } from '../MatchStatus/MatchStatus';
import { SportButtonFavorites } from '../SportButtonFavorites/SportButtonFavorites';
import { LineRowOddsCyber } from './components/LineRowOddsCyber/LineRowOddsCyber';
import { LineRowScoresCyber } from './components/LineRowScoresCyber/LineRowScoresCyber';
import { LineRowTranslation } from './components/LineRowTranslation/LineRowTranslation';
import styles from './LineRowCyber.module.scss';
export const LineRowCyber: FC<LineRowProps> = ({
  line: initialLine,
  isFavorite,
  className,
  isInSearch,
  analytics
}) => {
  const {
    push
  } = useRouter();
  const is1550Screen = useMediaWidthDown('screen1550');
  const is850Screen = useMediaWidthDown('screen850');
  const line = useWebsocketLineShort<LineWithEntities>(initialLine, base.NAME_PROJECT);
  const isAuth = useTypedSelector(selectUserIsAuthenticated);
  const selectedBets = useTypedSelector(selectCouponSelectedBetsV2);
  const isMatchEnded = getIsLineFinished(line);
  const selectedBet = selectedBets.map(({
    outcomeId
  }) => {
    const allOutcomes = line.topMarkets.flatMap(market => market.outcomes);
    return allOutcomes.find(outcome => outcome.id === outcomeId);
  }).find(Boolean);
  const {
    lineHref,
    competitors
  } = useLineBehaviorV2({
    line: initialLine,
    routes
  });
  const handleClick = useCallback<MouseEventHandler<HTMLDivElement>>(event => {
    const target = (event.target as HTMLElement);
    const odd = (target.closest(`.${styles.buttonWrapper}`) as HTMLSpanElement | null);
    const button = (target.closest('button') as HTMLButtonElement | null);
    const link = (target.closest('a') as HTMLAnchorElement | null);
    if (odd || button || link) {
      return;
    }
    if (analytics?.matchName) {
      sendAnalyticsData((analytics?.matchName as string), extendAnalyticsData(analytics?.matchName, {
        href: lineHref,
        text: getCompetitorsLineName(line),
        category: line.category.title,
        championships: line.championship.title,
        match_id: line.championshipId,
        line_id: line.id
      }));
    }
    push(lineHref);
  }, [analytics?.matchName, line, lineHref, push]);
  const LeftTeamComponent = <MatchTeam {...competitors.HOME} direction="rowReverse" sportCode={initialLine.sport.code} sportIcon={initialLine.sport.icon} className={styles.leftTeam} classes={{
    nameContainer: cn(styles.nameContainer, styles.leftTeamName),
    name: styles.name
  }} />;
  const RightTeamComponent = <MatchTeam {...competitors.AWAY} direction="row" sportCode={initialLine.sport.code} sportIcon={initialLine.sport.icon} className={styles.rightTeam} classes={{
    nameContainer: styles.nameContainer,
    name: styles.name
  }} />;
  const StatusComponent = <MatchStatus className={styles.matchStatus} beginAt={line.beginAt} stage={line.stage} status={line.status} currentSegment={line.stats?.currentSegment} clock={line.stats?.clock} isMatchEnded={isMatchEnded} />;
  const OddsComponent = <LineRowOddsCyber lineId={line.id} lineStage={line.stage} markets={line.topMarkets} linePaused={line.status !== 'OPENED'} lineHref={lineHref} classes={{
    buttonWrapper: styles.buttonWrapper
  }} analytics={{
    sportLinesOddSelect: analytics?.sportLinesOddSelect,
    sportLinesOddUnSelect: analytics?.sportLinesOddUnSelect
  }} isMatchEnded={isMatchEnded} />;
  const FavoritesWithWidgets = <div className={styles.info}>
      {isAuth && <SportButtonFavorites className={styles.favoriteButton} size="xs" lineId={line.id} isFavorite={isFavorite} analytics={extendAnalyticsData(isFavorite ? analytics?.removeFavoriteLine : analytics?.addFavoriteLine, {
      line_id: line.id,
      outcomes_id: selectedBet?.id,
      odds: selectedBet?.odds
    })} />}
      {!is1550Screen && StatusComponent}

      {!is1550Screen && <LineRowScoresCyber line={line} />}

      <LineRowTranslation line={line} analytics={analytics} />
    </div>;
  return <div role="presentation" className={cn(styles.root, styles[line.stage.toLowerCase()], className, {
    [styles.ended]: isMatchEnded,
    [styles.isInSearch]: isInSearch
  })} onClick={handleClick} data-sentry-component="LineRowCyber" data-sentry-source-file="LineRowCyber.tsx">
      {is850Screen && isInSearch ? null : FavoritesWithWidgets}

      {is1550Screen && <div className={styles.status}>
          {LeftTeamComponent}
          <div>
            {is850Screen && isInSearch ? FavoritesWithWidgets : null}
            {StatusComponent}
          </div>
          {RightTeamComponent}
        </div>}

      {is1550Screen && <LineRowScoresCyber line={line} />}

      <div className={styles.teams}>
        {!is1550Screen && LeftTeamComponent}
        {OddsComponent}
        {!is1550Screen && RightTeamComponent}
      </div>
    </div>;
};